import i18n from "i18next";
// Spec §6 — every sharing failure must carry three pieces:
// (1) what we tried, echoed back, (2) why it failed in plain
// language, (3) at least one recovery action. The six templates
// below cover all paths the drawer can take. Inline-bag a one-line
// red error in the UI is forbidden by the spec.
//
// Returned shape is render-agnostic so the same catalogue feeds
// the desktop drawer toast, mobile snackbar, and any future
// surface (CLI, screen reader). Pass it to <SharingErrorBanner/>.

export type SharingErrorKind =
  | "invalid-email"
  | "already-collaborator"
  | "network"
  | "quota"
  | "team-attach-failed"
  | "link-generate-failed";

export interface SharingErrorAction {
  label: string;
  /** Identifier consumed by the surface; the surface decides what
   *  primary/secondary actions actually do. Keeping it abstract so
   *  the same payload renders in toasts, modals, or RN snackbars. */
  kind:
    | "retry"
    | "copy-link"
    | "change-role"
    | "dismiss"
    | "manage-plan"
    | "remove-someone"
    | "pick-different-team"
    | "detach";
}

export interface SharingError {
  kind: SharingErrorKind;
  headline: string;
  body: string;
  actions: SharingErrorAction[];
}

interface BuildArgs {
  email?: string;
  role?: string;
  team?: string;
  quotaN?: number;
}

export function buildSharingError(
  kind: SharingErrorKind,
  args: BuildArgs = {},
): SharingError {
  const t = (key: string, opts?: Record<string, unknown>) => i18n.t(`sharing:errors.${key}`, opts);
  switch (kind) {
    case "invalid-email":
      return {
        kind,
        headline: t("invalidEmail.headline"),
        body: args.email ? t("invalidEmail.body", { email: args.email }) : t("invalidEmail.bodyPlain"),
        actions: [
          { label: t("actions.tryAgain"), kind: "dismiss" },
          { label: t("actions.copyDocLink"), kind: "copy-link" },
        ],
      };
    case "already-collaborator":
      return {
        kind,
        headline: t("already.headline"),
        body: args.email && args.role ? t("already.body", { email: args.email, role: args.role }) : t("already.bodyPlain"),
        actions: [
          { label: t("actions.changeRole"), kind: "change-role" },
          { label: t("dismiss"), kind: "dismiss" },
        ],
      };
    case "network":
      return {
        kind,
        headline: t("network.headline"),
        body: t("network.body"),
        actions: [
          { label: t("actions.retry"), kind: "retry" },
          { label: t("actions.copyDocLink"), kind: "copy-link" },
        ],
      };
    case "quota":
      return {
        kind,
        headline: t("quota.headline"),
        body: args.quotaN ? t("quota.body", { n: args.quotaN }) : t("quota.bodyPlain"),
        actions: [
          { label: t("actions.managePlan"), kind: "manage-plan" },
          { label: t("actions.removeSomeone"), kind: "remove-someone" },
        ],
      };
    case "team-attach-failed":
      return {
        kind,
        headline: t("team.headline", { team: args.team ?? t("team.thatTeam") }),
        body: t("team.body"),
        actions: [
          { label: t("actions.pickTeam"), kind: "pick-different-team" },
          { label: t("actions.detach"), kind: "detach" },
        ],
      };
    case "link-generate-failed":
      return {
        kind,
        headline: t("link.headline"),
        body: t("link.body"),
        actions: [{ label: t("actions.retry"), kind: "retry" }],
      };
  }
}

/** Heuristic classifier: HTTP layer failure → kind. Falls back to
 *  network so we never leave the user with a bare red string. */
export function classifyShareError(err: unknown, fallback: SharingErrorKind = "network"): SharingErrorKind {
  if (!err) return fallback;
  const msg = err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();
  if (msg.includes("already")) return "already-collaborator";
  if (msg.includes("email") || msg.includes("invalid")) return "invalid-email";
  if (msg.includes("quota") || msg.includes("limit")) return "quota";
  if (msg.includes("team")) return "team-attach-failed";
  if (msg.includes("link") || msg.includes("public")) return "link-generate-failed";
  return fallback;
}
